import { useMemo } from 'react';
import useSWR from 'swr';
import type { SWRConfiguration } from 'swr';

import { fetcher, endpoints } from '@/lib/axios';
import type { IFinancing, IFinancingsResponse } from '@/types/financing';

// ----------------------------------------------------------------------

const swrOptions: SWRConfiguration = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  shouldRetryOnError: false,
};

// ----------------------------------------------------------------------

/**
 * Fetches the active financing options shown on the "Financements" carousel.
 * Endpoint: GET /api/v1/financings
 */
export function useGetFinancings() {
  const { data, isLoading, error, isValidating } = useSWR<IFinancingsResponse>(
    endpoints.financings.list,
    fetcher,
    swrOptions
  );

  const memoizedValue = useMemo(
    () => ({
      financings: (data?.data ?? []) as IFinancing[],
      financingsLoading: isLoading,
      financingsError: error as Error | undefined,
      financingsValidating: isValidating,
      financingsEmpty: !isLoading && !(data?.data?.length),
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}
